import { Category } from 'lib-knowledge-management-common/models/types';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';

export type ParentCategoryListItem = {
  name: string;
  id: string;
};

export type ReduceCategoryResult = {
  listItems: ParentCategoryListItem[];
  allDisabledCategoryIds: string[];
};
export type ReduceCategoryParams = {
  nestLevel: number;
  disabledCategoryIds: string[];
  currentlySelectedId: string | undefined;
};

type InitialReduceCategoryResult = {
  listItems: ParentCategoryListItem[];
  allDisabledCategoryIds: string[];
};
export const initialReduceCategoryResult = (
  collectionId?: string,
): InitialReduceCategoryResult => ({
  listItems: collectionId
    ? [
        {
          id: collectionId,
          name: 'None',
        },
      ]
    : [],
  allDisabledCategoryIds: [],
});

type ExpandedListItem = {
  type: 'Collection' | 'Category';
  name: string;
  id: string;
  disabled: boolean;
  nestLevel: number;
};

type Accumulator = {
  listItems: ExpandedListItem[];
  nestLevel: number;
};

const reduceCategories =
  ({
    currentCategoryId,
    disabledCategoryIds,
  }: {
    /** The id of a category we are moving / editing, otherwise undefined. */
    currentCategoryId: string | undefined;
    disabledCategoryIds: ReadonlyArray<string>;
  }) =>
  (accumulator: Accumulator, category: Category): Accumulator => {
    const isDisabled =
      category.id === currentCategoryId ||
      disabledCategoryIds.includes(category.id);

    const categoryTitle = extractTitleFromTranslations(category.titles);
    accumulator.listItems.push({
      disabled: isDisabled,
      id: category.id,
      type: category.__typename,
      name: categoryTitle,
      nestLevel: accumulator.nestLevel,
    });

    // Don't add disabled children to the list.
    !isDisabled &&
      // Because we only fetch X levels of categories.
      category.categories?.reduce(
        reduceCategories({
          disabledCategoryIds,
          currentCategoryId,
        }),
        {
          listItems: accumulator.listItems,
          nestLevel: accumulator.nestLevel + 1,
        },
      );

    return accumulator;
  };

export function getCategoryDropdownItems({
  categories,
  currentCategoryId,
  disabledCategoryIds,
  withCollectionOption,
  collectionId,
}: {
  currentCategoryId: string | undefined;
  disabledCategoryIds: string[];
  categories: Category[];
  withCollectionOption: boolean;
  collectionId: string | undefined;
}): ReduceCategoryResult {
  if (!collectionId) throw new Error('collectionId is required');

  const flatCategories = categories.reduce(
    reduceCategories({
      currentCategoryId,
      disabledCategoryIds,
    }),
    {
      listItems: withCollectionOption
        ? [
            {
              name: 'None',
              type: 'Collection',
              id: collectionId,
              disabled: false,
              nestLevel: -1,
            },
          ]
        : [],
      nestLevel: 0,
    },
  ).listItems;

  return {
    listItems: flatCategories.map((item) => ({
      id: item.id,
      name: indentName(item.name, item.nestLevel),
    })),

    allDisabledCategoryIds: flatCategories
      .filter((item) => item.disabled)
      .map((item) => item.id),
  };
}

export const singleIndent = '\u00A0\u00A0\u00A0\u00A0';
function indentName(name: string, nestLevel: number): string {
  const indentation = Array.from({ length: nestLevel })
    .map(() => singleIndent)
    .join('');

  return `${indentation}${name}`;
}
